// Where the off-screen move marker sits, and which move it is about.
//
// **The pure half of the marker, because it is the half worth testing.** The
// zone needs a renderer, a camera and the inset registry; this needs a point
// and a rectangle. Same split as `hintPlacement.ts`.
//
// The marker exists because `trackPiece` is off by default (WP1): the camera no
// longer flies to the opponent's move, so something has to say where it
// happened. A chevron at the edge of the free area, on the ray toward the
// square, is the smallest thing that says it — and it is a *control*, not a
// notice, because every camera action has to be reachable by a touch.

/**
 * What the marker reads: the moves played, as the board on screen holds them.
 * Structural, so the one caller can hand it the shown state without the
 * client naming the engine's type — the contract has no last-move fact yet.
 */
export interface Played {
  history: readonly { from: number; to: number }[];
}

/** The two squares of the move the marker is about. */
export interface MarkedMove {
  from: number;
  to: number;
}

/**
 * The move the board last played, or null before there is one.
 *
 * `state` is the **viewed** board everywhere this is called (`useGameState`),
 * so rewinding the transport walks the marker back with it — which is right:
 * the marker points at the move on screen, not at the move in the log.
 */
export function lastMove(state: Played): MarkedMove | null {
  const move = state.history[state.history.length - 1];
  return move ? { from: move.from, to: move.to } : null;
}

/** A point in viewport pixels. */
export interface Point {
  x: number;
  y: number;
}

/** The free area's edges, in screen px. `Rect`'s other spelling. */
export interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface EdgePlacement {
  x: number;
  y: number;
  /** Degrees, for a CSS `rotate` of a **right-pointing** chevron. */
  angle: number;
}

const clamp = (value: number, low: number, high: number): number =>
  Math.max(low, Math.min(high, value));

/**
 * Where to put the marker for a square at `point`, or null when it is on
 * screen and the marker must not be drawn at all.
 *
 * The ray runs from the free area's **centre** to the point and leaves the
 * rectangle inset by `margin` — so the marker sits inside the area the chrome
 * is not covering, and the player follows the chevron out of the screen toward
 * the square. Inside-ness is tested against the free area itself rather than
 * the inset one: the margin is where the marker may be *drawn*, not what
 * counts as off screen, and a square in the margin band is one you can see.
 */
export function edgePlacement(
  point: Point,
  free: Bounds,
  margin: number
): EdgePlacement | null {
  if (
    point.x >= free.left &&
    point.x <= free.right &&
    point.y >= free.top &&
    point.y <= free.bottom
  ) {
    return null;
  }

  const cx = (free.left + free.right) / 2;
  const cy = (free.top + free.bottom) / 2;
  const dx = point.x - cx;
  const dy = point.y - cy;

  // A margin wider than the area itself collapses to the centre line rather
  // than inverting the rectangle, which would clamp every marker to the wrong
  // edge in silence.
  const left = Math.min(free.left + margin, cx);
  const right = Math.max(free.right - margin, cx);
  const top = Math.min(free.top + margin, cy);
  const bottom = Math.max(free.bottom - margin, cy);

  // How far along the ray each axis' bound is, and the nearer one is the edge
  // it leaves through.
  const tx = dx === 0 ? Infinity : ((dx > 0 ? right : left) - cx) / dx;
  const ty = dy === 0 ? Infinity : ((dy > 0 ? bottom : top) - cy) / dy;
  const t = Math.min(tx, ty);

  return {
    // Clamped as well as solved: the nearer bound is exact and the other is
    // inside by construction, but floating point does not owe us the corner.
    x: clamp(cx + dx * t, left, right),
    y: clamp(cy + dy * t, top, bottom),
    angle: (Math.atan2(dy, dx) * 180) / Math.PI
  };
}
